/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import dayjs from "dayjs";
import { Col, Row } from "react-awesome-styled-grid";
import { When } from "react-if";
import styled from "styled-components";
import {
  Box,
  Button,
  ExtendedTooltip,
  FlexContainer,
  Text,
  theme,
  ModalConfirm,
} from "@nordcloud/gnui";
import { Plan, GroupForResource } from "~/generated/graphql";
import { ExpandIcon } from "~/components";
import { TableWrapper } from "~/components/Tables";
import { NameWithLink } from "~/components/Tables/cells/NameWithLink";
import { dateFormat } from "~/constants";
import { useToggle } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { showSuccess } from "~/services/toast";
import { useUpdateResourceGroupInResource } from "~/views/resourceGroups/hooks/useUpdateResourceGroup/useUpdateResourceGroupInResource";
import { columns } from "./ResourceDetailsResourceGroupPlansColumns";
import { ResourceDetailsResourceGroupsGridColumns } from "./ResourceDetailsResourceGroupsGridColumns";

type Props = {
  resourceGroup: GroupForResource;
  resourceName: string;
  refetch: () => void;
};

export function ResourceDetailsResourceGroupItem({
  resourceGroup,
  resourceName,
  refetch,
}: Props) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [
    isExcludeFromResourceGroupModalOpen,
    setIsExcludeFromResourceGroupModalOpen,
  ] = useState(false);

  const numberOfPlans = resourceGroup?.plans?.length ?? 0;
  const hasPlans = numberOfPlans > 0;
  const [isExpanded, toggleIsExpanded] = useToggle();

  const { updateGroup } = useUpdateResourceGroupInResource({
    onSuccess: () => {
      showSuccess("Resource successfully removed");
      refetch();
    },
  });

  return (
    <Box spacing="spacing02" boxStyle="lightGrey">
      <Row>
        <Col>
          <ResourceDetailsResourceGroupsGridColumns>
            <FlexContainer justifyContent="center" alignItems="center">
              <When condition={hasPlans}>
                <ExpandIcon
                  name="chevronDown"
                  title="chevronDown"
                  isExpanded={isExpanded}
                  onClick={toggleIsExpanded}
                />
              </When>
            </FlexContainer>
            <NameWithLink
              name={resourceGroup.name}
              link={ROUTES.resourceGroups.generateDetails(resourceGroup.id)}
              css={{ paddingLeft: theme.spacing.spacing03 }}
              containerStyle={{ paddingRight: theme.spacing.spacing03 }}
            />
            <FlexContainer alignItems="center">
              <Text
                size="md"
                tag="span"
                mr={theme.spacing.spacing01}
                weight="regular"
                color={theme.color.text.text01}
              >
                {dayjs(resourceGroup.createdAt).format(
                  dateFormat.dayMonthYearHourMinute
                )}
              </Text>
            </FlexContainer>
            <FlexContainer alignItems="center">
              <Text
                size="md"
                tag="span"
                mr={theme.spacing.spacing01}
                weight="regular"
                color={theme.color.text.text01}
              >
                {dayjs(resourceGroup.updatedAt).format(
                  dateFormat.dayMonthYearHourMinute
                )}
              </Text>
            </FlexContainer>
            <FlexContainer alignItems="center">
              <Text
                size="md"
                tag="span"
                mr={theme.spacing.spacing01}
                weight="regular"
                color={theme.color.text.text01}
              >
                {resourceGroup.createdBy}
              </Text>
            </FlexContainer>
            <FlexContainer alignItems="center">
              <Text
                size="md"
                tag="span"
                mr={theme.spacing.spacing01}
                weight="regular"
                color={theme.color.text.text01}
              >
                {numberOfPlans}
              </Text>
            </FlexContainer>
            <FlexContainer alignItems="center" justifyContent="end">
              <ExtendedTooltip
                caption={
                  !resourceGroup.resourceSelector.tagsExpression
                    ? "Exclude resource from the group"
                    : "Cannot exclude: added by tag expression, not manually"
                }
              >
                <Button
                  disabled={!!resourceGroup.resourceSelector.tagsExpression}
                  size="md"
                  icon="brokenLink"
                  severity="low"
                  onClick={() => setIsExcludeFromResourceGroupModalOpen(true)}
                />
              </ExtendedTooltip>
            </FlexContainer>
          </ResourceDetailsResourceGroupsGridColumns>
          <When condition={isExpanded}>
            <PlansTableWrapper>
              <TableWrapper<Partial<Plan>>
                loading={false}
                data={resourceGroup.plans ?? []}
                columns={columns()}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                stripped
              />
            </PlansTableWrapper>
          </When>
          <ModalConfirm
            onClose={() => setIsExcludeFromResourceGroupModalOpen(false)}
            isOpen={isExcludeFromResourceGroupModalOpen}
            confirm={() => {
              setIsExcludeFromResourceGroupModalOpen(false);
              updateGroup({
                id: resourceGroup.id,
                resourceSelectorsToDelete: [resourceGroup.resourceSelector.id],
              });
            }}
            actionLabel="Exclude"
            contentLabel={`Exclude Resource from the Resource Group`}
          >
            <Text>
              Are you sure you want to exclude <b>{resourceName}</b> from the{" "}
              <b>{resourceGroup.name}</b>?{" "}
              {numberOfPlans > 0
                ? `Resource group is used in the plan(s) below:`
                : ""}
            </Text>
            <When condition={numberOfPlans > 0}>
              <Box boxStyle="lightGrey" spacing="spacing04">
                {resourceGroup.plans?.map((plan) => (
                  <Text marginBottom={0} key={plan.id}>
                    {plan.name}
                  </Text>
                ))}
              </Box>
            </When>
          </ModalConfirm>
        </Col>
      </Row>
    </Box>
  );
}

const PlansTableWrapper = styled.div`
  & > table > thead > tr > th:nth-child(2) {
    padding-left: 0 !important;
  }
  & > table > tbody > tr > td:nth-child(2) {
    padding-left: 0 !important;
  }
`;
